
import { Box, useTheme } from '@mui/material'
import { WaveTabButton } from './WaveTabButton'
import type { ReactNode } from 'react'
import React from 'react'
import type { WaveSidebarTabProps } from './WaveSidebar'

export interface TabConfig {
  /** Unique identifier for the tab */
  id: string
  
  /** Icon to display in the tab button */
  icon: ReactNode
  
  /** Label for tooltip and accessibility */
  label: string
  
  /** Whether the tab is disabled */
  disabled?: boolean
}

export interface WaveTabBarProps {
  /** Currently active tab ID */
  activeTab: string
  
  /** Whether the sidebar is collapsed (only tab bar visible) */
  collapsed?: boolean
  
  /** Callback when tab changes */
  onTabChange: (tabId: string) => void
  
  /** Callback when sidebar should be collapsed/expanded */
  onCollapsedChange?: (collapsed: boolean) => void
  
  /** WaveSidebar.Tab children to extract tab metadata from */
  children: ReactNode
  
  /** Optional className for styling */
  className?: string
}

/**
 * Extract tab configurations from WaveSidebar.Tab children
 */
const extractTabsFromChildren = (children: ReactNode): TabConfig[] => {
  const tabs: TabConfig[] = []
  
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      // Handle React Fragments
      if (child.type === React.Fragment) {
        // Recursively process Fragment children
        tabs.push(...extractTabsFromChildren((child.props as any).children))
      } else if ((child.props as any).id) {
        // Direct WaveSidebar.Tab component
        const props = child.props as WaveSidebarTabProps
        tabs.push({
          id: props.id,
          icon: props.icon,
          label: props.label,
          disabled: props.disabled || false
        })
      }
    }
  })
  
  return tabs
}

/**
 * Vertical tab bar for the Wave sidebar
 * Contains multiple WaveTabButton components in a VS Code-style layout
 */
export function WaveTabBar({
  activeTab,
  collapsed = false,
  onTabChange,
  onCollapsedChange,
  children,
  className
}: WaveTabBarProps) {
  const theme = useTheme()
  
  // Extract tab configurations from children
  const tabs = extractTabsFromChildren(children)
  
  const handleTabClick = (tabId: string) => {
    if (collapsed) {
      // If sidebar is collapsed, expand it and switch to the clicked tab
      onCollapsedChange?.(false)
      onTabChange(tabId)
    } else if (activeTab === tabId) {
      // If clicking on the currently active tab, collapse the sidebar (VS Code behavior)
      onCollapsedChange?.(true)
    } else {
      // Switch to the clicked tab
      onTabChange(tabId)
    }
  }
  
  return (
    <Box
      className={className}
      sx={{
        width: 60,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.mode === 'dark' 
          ? theme.palette.grey[900] 
          : theme.palette.grey[100],
        borderRight: 1,
        borderColor: 'divider',
        flexShrink: 0,
        py: 1
      }}
    >
      {tabs.map((tab) => (
        <WaveTabButton
          key={tab.id}
          icon={tab.icon}
          label={tab.label}
          active={!collapsed && activeTab === tab.id}
          disabled={tab.disabled}
          onClick={() => handleTabClick(tab.id)}
        />
      ))}
    </Box>
  )
} 